import { useMutation } from '@tanstack/react-query';
import { Col, DatePicker, Form, Modal, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { DefaultOptionType } from 'antd/es/select';
import { ExpensesApis } from 'apis/ExpensesApis';
import { ICreateExpense, IGetRoutes } from 'apis/GraphQL/Variables/expenses';
import { InputForm, SelectForm } from 'components';
import InputNumberForm from 'components/Input/InputNumberForn';
import { useUser } from 'contexts/UserProvider';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import formatNumber, {
  toastError,
  toastSuccess,
} from 'utils/constants/functions';

export interface IAddRouteCostProps {
  defaultFilter: any;
  open: boolean;
  onClose: () => void;
  refetch: () => void;
}

export interface ICreateExpenseForm {
  startDate: string;
  customerId: string;
  routeId: string;
  teamSalesId: string;
  userId: string;
  expensesType: string;
  policyExpenses: number;
  actualExpenses: number;
  numberPerson: number;
  finalExpenses: number;
  routeName: string;
  policy: number;
  note: string;
}

const AddRouteCost = ({
  defaultFilter,
  open,
  onClose,
  refetch,
}: IAddRouteCostProps) => {
  const { user } = useUser();
  const [form] = useForm<ICreateExpenseForm>();
  const [expenseTypes, setExpenseTypes] = useState<DefaultOptionType[]>([]);
  const [routes, setRoutes] = useState<Array<DefaultOptionType>>([]);

  const { mutate: createExpense, isPending: createExpenseLoading } =
    useMutation({
      mutationFn: (params: ICreateExpense) =>
        ExpensesApis.createExpense(params),
      onSuccess: () => {
        refetch();
        onClose();
        toastSuccess('Tạo chi phí thành công');
      },
      onError: (error) => {
        console.log(error);
        toastError('Tạo chi phí thất bại');
      },
    });

  const { mutate: getAllExpensesType, isPending: getAllExpensesTypeLoading } =
    useMutation({
      mutationFn: () => ExpensesApis.getAllExpensesType(),
      onSuccess: (data) => {
        const types = data.data?.getExpensesType?.items ?? [];

        setExpenseTypes(
          types.map((type: any) => ({
            label: type.name,
            value: type.id,
            expensesAmount: type.expensesAmount,
          })),
        );
      },
    });

  const { mutate: getRoutes, isPending: getRoutesLoading } = useMutation({
    mutationFn: (params: IGetRoutes) => ExpensesApis.getRoutes(params),
    onSuccess: (data) => {
      const result = data.data?.getSalesPlanningList?.items || [];

      setRoutes(
        result.map((route: any) => ({
          label: route.routeName,
          value: route.routeId,
        })),
      );
    },
  });

  const handleChangeFinalExpenses = () => {
    const actualExpenses = form.getFieldValue('actualExpenses');
    const numberPerson = form.getFieldValue('numberPerson');

    if (actualExpenses !== undefined && numberPerson !== undefined) {
      form.setFieldsValue({
        finalExpenses: actualExpenses * numberPerson,
      });
    }
  };

  const validate = async () => {
    try {
      await form.validateFields();
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleCreate = async () => {
    const isValid = await validate();

    const teamSaleId = user?.teamSales.at(0)?.id;

    if (!teamSaleId) {
      toastError('Không tìm thấy thông tin đội bán hàng');
      return;
    }

    if (!isValid) return;
    if (!user) return;

    createExpense({
      actualExpenses: form.getFieldValue('actualExpenses'),
      expensesType: form.getFieldValue('expensesType'),
      customerId: defaultFilter.customerId,
      finalExpenses: form.getFieldValue('finalExpenses'),
      note: form.getFieldValue('note'),
      numberPerson: form.getFieldValue('numberPerson'),
      policyExpenses: form.getFieldValue('policy'),
      routeId: form.getFieldValue('routeId'),
      startDate: form.getFieldValue('startDate').format('YYYY-MM-DD'),
      teamSalesId: teamSaleId,
      userId: user?.id,
      plantId: defaultFilter.plantId,
    });
  };

  const handleChangeRoute = (_value: any, item: any) => {
    form.setFieldsValue({
      routeName: item?.label || undefined,
    });
  };

  const handleChangeExpensesType = (_value: any, item: any) => {
    form.setFieldsValue({
      policy: item?.expensesAmount || undefined,
    });
  };

  const handleChangeDate = () => {
    const date = form.getFieldValue('startDate');

    getRoutes({
      implementationDateFrom: dayjs(date).format('YYYY-MM-DD 00:00:00'),
      plantId: defaultFilter.plantId,
      skip: 0,
      take: 100,
      teamId: defaultFilter.teamSalesSubId,
    });

    form.setFieldsValue({
      routeId: undefined,
      routeName: undefined,
    });
  };

  useEffect(() => {
    getAllExpensesType();
  }, [getAllExpensesType]);

  useEffect(() => {
    return () => {
      form.resetFields();
    };
  }, [form, open]);

  return (
    <Modal
      centered
      open={open}
      title="Khai báo chi phí"
      width={800}
      okText="Tạo"
      cancelText="Hủy"
      onCancel={onClose}
      onOk={handleCreate}
      confirmLoading={createExpenseLoading}
    >
      <Form form={form} layout="vertical">
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              label="Ngày đi"
              name="startDate"
              rules={[
                {
                  required: true,
                  message: 'Vui lòng chọn ngày đi',
                },
              ]}
            >
              <DatePicker
                style={{
                  width: '100%',
                }}
                placeholder="Chọn ngày đi"
                format="DD/MM/YYYY"
                maxDate={dayjs()}
                onChange={handleChangeDate}
              />
            </Form.Item>
          </Col>
          <Col span={12} />
          <Col span={12}>
            <Form.Item
              label="Mã tuyến"
              name="routeId"
              rules={[
                {
                  required: true,
                  message: 'Vui lòng chọn mã tuyến',
                },
              ]}
            >
              <SelectForm
                options={routes}
                loading={getRoutesLoading}
                placeholder="Chọn tuyến"
                size="middle"
                onChange={handleChangeRoute}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Tên tuyến" name="routeName">
              <InputForm placeholder="Tên tuyến" readOnly size="middle" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Loại chi phí"
              name="expensesType"
              rules={[
                {
                  required: true,
                  message: 'Vui lòng chọn loại chi phí',
                },
              ]}
            >
              <SelectForm
                options={expenseTypes}
                loading={getAllExpensesTypeLoading}
                placeholder="Chọn loại chi phí"
                size="middle"
                onChange={handleChangeExpensesType}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Chính sách công ty" name="policy">
              <InputNumberForm
                placeholder="Chính sách công ty"
                readOnly
                addonAfter="VND"
                size="middle"
                formatter={(value: any) => {
                  return value ? formatNumber(value) : '';
                }}
                parser={(value: any) => {
                  return parseFloat(
                    value.replace(/\./g, '')?.replace(',', '.'),
                  );
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Chi phí thực tế"
              name="actualExpenses"
              rules={[
                {
                  required: true,
                  message: 'Vui lòng nhập chi phí thực tế',
                },
              ]}
            >
              <InputNumberForm
                placeholder="Nhập chi phí thực tế"
                min={0}
                addonAfter="VND"
                size="middle"
                onChange={handleChangeFinalExpenses}
                formatter={(value: any) => {
                  return value ? formatNumber(value) : '';
                }}
                parser={(value: any) => {
                  return parseFloat(
                    value.replace(/\./g, '')?.replace(',', '.'),
                  );
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Số lượng"
              name="numberPerson"
              rules={[
                {
                  required: true,
                  message: 'Vui lòng nhập chi phí thực tế',
                },
              ]}
            >
              <InputNumberForm
                placeholder="Nhập số lượng"
                min={1}
                size="middle"
                onChange={handleChangeFinalExpenses}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Thành tiền" name="finalExpenses">
              <InputNumberForm
                placeholder="Thành tiền"
                readOnly
                min={0}
                addonAfter="VND"
                size="middle"
                formatter={(value: any) => {
                  return value ? formatNumber(value) : '';
                }}
                parser={(value: any) => {
                  return parseFloat(
                    value.replace(/\./g, '')?.replace(',', '.'),
                  );
                }}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Ghi chú" name="note">
              <InputForm placeholder="Nhập ghi chú" size="middle" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddRouteCost;
